import React, { useMemo, useState } from 'react';

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

function scaleMacro({ targetUnits, referenceUnits, fieldValue }) {
  const t = Number(targetUnits);
  const r = Number(referenceUnits);
  const f = Number(fieldValue);

  if (!Number.isFinite(t) || !Number.isFinite(r) || !Number.isFinite(f)) return null;
  if (r === 0) return null;

  return (t * f) / r;
}

function kcalFromMacros({ carbs, protein, fat }) {
  const c = Number(carbs);
  const p = Number(protein);
  const f = Number(fat);
  if (!Number.isFinite(c) || !Number.isFinite(p) || !Number.isFinite(f)) return null;
  // sugars are assumed to be included in carbs
  return (4 * c) + (4 * p) + (9 * f);
}

export function __testables__() {
  return { toNumberOrNull, scaleMacro, kcalFromMacros };
}

export default function MacroCalcPanel({ onClose = null, onOk = null, initialValues = null }) {
  // Inputs represent values for `referenceUnits` (e.g. per 100g).
  const [carbs, setCarbs] = useState(initialValues?.carbs ?? '');
  const [sugar, setSugar] = useState(initialValues?.sugar ?? '');
  const [protein, setProtein] = useState(initialValues?.protein ?? '');
  const [fat, setFat] = useState(initialValues?.fat ?? '');
  const [kcal, setKcal] = useState(initialValues?.kcal ?? '');
  const [referenceUnits, setReferenceUnits] = useState(initialValues?.referenceUnits ?? '100');
  const [targetUnits, setTargetUnits] = useState(initialValues?.targetUnits ?? '');
  const [unitName, setUnitName] = useState(initialValues?.unitName ?? '');

  const parsed = useMemo(() => {
    const ref = toNumberOrNull(referenceUnits);
    const target = toNumberOrNull(targetUnits);

    return {
      carbs: toNumberOrNull(carbs),
      sugar: toNumberOrNull(sugar),
      protein: toNumberOrNull(protein),
      fat: toNumberOrNull(fat),
      kcal: toNumberOrNull(kcal),
      referenceUnits: ref,
      targetUnits: target,
      unitName: String(unitName ?? '').trim(),
    };
  }, [carbs, sugar, protein, fat, kcal, referenceUnits, targetUnits, unitName]);

  const result = useMemo(() => {
    if (parsed.referenceUnits === null || parsed.targetUnits === null) {
      return {
        carbs: null,
        sugar: null,
        protein: null,
        fat: null,
        kcal: null,
      };
    }

    return {
      carbs: parsed.carbs === null ? null : scaleMacro({ targetUnits: parsed.targetUnits, referenceUnits: parsed.referenceUnits, fieldValue: parsed.carbs }),
      sugar: parsed.sugar === null ? null : scaleMacro({ targetUnits: parsed.targetUnits, referenceUnits: parsed.referenceUnits, fieldValue: parsed.sugar }),
      protein: parsed.protein === null ? null : scaleMacro({ targetUnits: parsed.targetUnits, referenceUnits: parsed.referenceUnits, fieldValue: parsed.protein }),
      fat: parsed.fat === null ? null : scaleMacro({ targetUnits: parsed.targetUnits, referenceUnits: parsed.referenceUnits, fieldValue: parsed.fat }),
      kcal: parsed.kcal === null ? null : scaleMacro({ targetUnits: parsed.targetUnits, referenceUnits: parsed.referenceUnits, fieldValue: parsed.kcal }),
    };
  }, [parsed]);

  const kcalHintFromMacros = useMemo(() => {
    return kcalFromMacros({ carbs: parsed.carbs ?? null, protein: parsed.protein ?? null, fat: parsed.fat ?? null });
  }, [parsed.carbs, parsed.protein, parsed.fat]);

  const hasValidCalc = useMemo(() => {
    return (
      parsed.referenceUnits !== null &&
      parsed.targetUnits !== null &&
      parsed.referenceUnits !== 0
    );
  }, [parsed.referenceUnits, parsed.targetUnits]);

  const inputStyle = {
    width: '100%',
    fontSize: '1.05rem',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontSize: '0.85rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    color: '#333',
    margin: '0 0 6px 2px',
    userSelect: 'none'
  };

  const fieldBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const grid3Style = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 10,
    alignItems: 'start'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '90px'
  };

  const formatMaybe = (v, digits = 2) => {
    if (v === null || v === undefined || !Number.isFinite(v)) return '—';
    // Keep a small number of decimals, but avoid trailing zeros explosion.
    const rounded = Number(v.toFixed(digits));
    return String(rounded);
  };

  const handleOk = () => {
    if (typeof onOk !== 'function') return;
    // Prefer returning target-scaled results; fall back to nulls if invalid.
    onOk({
      sugar: hasValidCalc ? result.sugar : null,
      carbs: hasValidCalc ? result.carbs : null,
      protein: hasValidCalc ? result.protein : null,
      fat: hasValidCalc ? result.fat : null,
      kcal: hasValidCalc ? result.kcal : null,
      referenceUnits: parsed.referenceUnits,
      targetUnits: parsed.targetUnits,
      unitName: parsed.unitName,
    });
  };

  return (
    <div style={{
      padding: '10px 0',
      maxWidth: '520px',
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      <div>
        <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>🧮 Macro Calc</h3>

        {/* Inputs grid (3 columns) */}
        <div style={{ ...grid3Style, marginBottom: 14 }}>
          {/* Row 1: sugar, carbs, protein */}
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>S</div>
            <input type="number" value={sugar} onChange={(e) => setSugar(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>C</div>
            <input type="number" value={carbs} onChange={(e) => setCarbs(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>P</div>
            <input type="number" value={protein} onChange={(e) => setProtein(e.target.value)} style={inputStyle} />
          </div>

          {/* Row 2: fat, kcal, empty */}
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>F</div>
            <input type="number" value={fat} onChange={(e) => setFat(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>kcal</div>
            <input type="number" value={kcal} onChange={(e) => setKcal(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>counted kcal</div>
            <div
              style={{
                ...inputStyle,
                borderStyle: 'dashed',
                borderColor: '#ff8a00',
                background: '#fff',
                fontWeight: 800,
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {formatMaybe(kcalHintFromMacros)}
            </div>
          </div>

          {/* Row 3: ref units, empty, intake */}
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>ref units</div>
            <input type="number" value={referenceUnits} onChange={(e) => setReferenceUnits(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>unit</div>
            <input type="text" value={unitName} onChange={(e) => setUnitName(e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldBoxStyle}>
            <div style={labelStyle}>intake</div>
            <input type="number" value={targetUnits} onChange={(e) => setTargetUnits(e.target.value)} style={inputStyle} />
          </div>
        </div> 

        {/* Results grid (3 columns) */}
        <div style={{
          padding: '12px 14px',
          borderRadius: 10,
          border: '1px solid #ff8a00',
          background: '#fff3e6',
          marginBottom: 10,
        }}>
          {!hasValidCalc ? (
            <div style={{ color: '#666', fontSize: '0.95rem', fontWeight: 600 }}>
              Enter non-zero <b>ref units</b> and <b>intake</b>.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontWeight: 800 }}>
              {/* Row 1: sugar, carbs, protein */}
              <div>S: <b>{formatMaybe(result.sugar)}</b></div>
              <div>C: <b>{formatMaybe(result.carbs)}</b></div>
              <div>P: <b>{formatMaybe(result.protein)}</b></div>

              {/* Row 2: fat, kcal, empty */}
              <div>F: <b>{formatMaybe(result.fat)}</b></div>
              <div>kcal: <b>{formatMaybe(result.kcal)}</b></div>
              <div />
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 'auto' }}>
        <button
          onClick={handleOk}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white',
            width: '100%'
          }}
        >
          OK
        </button>
        <div />
        <button
          onClick={() => { if (onClose) onClose(); }}
          style={{
            ...buttonStyle,
            backgroundColor: '#ff8a00',
            color: 'white',
            width: '100%'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
